import {Button, Checkbox, Select, Spin, Table, Tooltip, Typography} from "antd";
import {CloseOutlined, EyeInvisibleOutlined, EyeOutlined, TableOutlined} from "@ant-design/icons";
import {useEffect, useState} from "react";
import FileHelper from "../../Helpers/FileReader";
import ApiService from "../../Service/ApiService";
import {unzipToJson} from "../ProjectUploader";
import {v4} from "uuid";
import ShapeFileHelper from "../../Helpers/ShapeFileHelper";
import {useModal} from "../../Context/ModalContext";
import ShapeAttributeTable from "./ShapeAttributeTable";
import d from "d";
import {da} from "plotly.js/src/traces/carpet/attributes";

export default function ShapeTable({shapes, onChange, selectedLayers, onSelected, bounding, selectable}) {
    const [disable, setDisable] = useState(null)
    const [data, setData] = useState({
        shapes: shapes,
        isChanged: false
    })
    const {openModal} = useModal()

    const removeShapes = (row) => {
        setData({
            isChanged: v4(),
            shapes: data.shapes.filter(x => x.id !== row.id)
        })
    }

    const convertToGeoJson = async (row) => {
        setDisable(row.id)
        try {
            const shapeFiles = [...data.shapes]
            const index = shapeFiles.findIndex(x => x.id === row.id)
            if(index > -1) {
                const shapeFile = shapeFiles[index]
                const base64 = await FileHelper.readAsBase64(shapeFile.data)
                const res = await ApiService.RasCheck.ShpToGeoJson({
                    zip: base64,
                    type: shapeFile.ext
                })

                if(res.success){
                    const geojson = await unzipToJson(JSON.parse(res.data)?.result)
                    shapeFiles[index] =  {
                        fileName: `${shapeFile.fileName}.geojson`,
                        ext: 'geojson',
                        title: `${shapeFile.fileName}.geojson`,
                        data: geojson,
                        type: 'shape-file',
                    }

                    setData({
                        isChanged: v4(),
                        shapes: [...shapeFiles]
                    })
                }
            }
        } catch (exp) {console.error(exp)}
        setDisable(null)
    }

    const setLabels = (row, values) => {
        const shapeFiles = [...data.shapes]
        const index = shapeFiles.findIndex(x => x.id === row.id)
        if(index > -1) {
            const shapeFile = shapeFiles[index]
            shapeFile.labels = values || []
            setData({
                isChanged: v4(),
                shapes: [...shapeFiles]
            })
        }
    }

    const setShowLabels = (row) => {
        const shapeFiles = [...data.shapes]
        const index = shapeFiles.findIndex(x => x.id === row.id)
        if(index > -1) {
            const shapeFile = shapeFiles[index]
            shapeFile.showLabels = !shapeFile.showLabels
            setData({
                isChanged: v4(),
                shapes: [...shapeFiles]
            })
        }
    }

    const getColumns = () => {
        let columns = [
            {
                key: '#',
                dataIndex: '#',
                title: '',
                width: 48,
                render: (text, row) => {
                    return (
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <Button
                                size={'small'}
                                icon={<CloseOutlined style={{fontSize: '7pt'}}/>}
                                onClick={() => {
                                    removeShapes(row)
                                }}
                                type={'text'}
                            />
                            {
                                selectable && (
                                    <Checkbox
                                        checked={selectedLayers.find(x => x.id === row?.id)}
                                        onChange={(e) => {
                                            const _selected = [...selectedLayers]
                                            if(e.target.checked) {
                                                onSelected && onSelected([..._selected, row])
                                            }
                                            else {
                                                onSelected && onSelected(_selected.filter(x => x.id !== row.id))
                                            }
                                        }}
                                    />
                                )
                            }
                        </div>
                    )
                }
            },
            {
                key: 'fileName',
                dataIndex: 'fileName',
                title: 'Layers',
                width: selectable ? 325 : '',
                render: (text, row) => (
                    <Typography.Text style={{width: 325}} ellipsis={true}>
                        {text}
                    </Typography.Text>
                )
            }
        ]

        if(selectable) {
            columns = [
                ...columns,
                {
                    key: 'Label',
                    dataIndex: 'label',
                    title: 'Label',
                    render: (text, row) => {
                        if(!ShapeFileHelper.isGeoJson(row?.fileName))
                            return (
                                <div style={{
                                    display: !ShapeFileHelper.isGeoJson(row?.fileName) ? 'flex' : 'none',
                                    alignItems: 'center',
                                    gap: 8
                                }}>
                                    <Button
                                        disabled={disable}
                                        size={'small'}
                                        type={'primary'}
                                        onClick={() => convertToGeoJson(row)}
                                    >
                                        Process
                                    </Button>
                                    <Spin spinning={disable === row?.id} size={'small'}/>
                                </div>
                            )

                        return (
                            <div key={row?.fileName} style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                {/*<Button*/}
                                {/*    icon={row?.showLabels ? <EyeInvisibleOutlined /> : <EyeOutlined />}*/}
                                {/*    size={'small'}*/}
                                {/*    onClick={() => {*/}
                                {/*        setShowLabels(row)*/}
                                {/*    }}*/}
                                {/*/>*/}
                                <Select
                                    style={{width: '100%'}}
                                    size={'small'}
                                    allowClear={true}
                                    mode={'multiple'}
                                    value={row.labels || []}
                                    onChange={(values) => setLabels(row, values)}
                                >
                                    {
                                        ShapeFileHelper.getPropertyNames(row?.data).map(attr => (
                                            <Select.Option value={attr}>
                                                {attr}
                                            </Select.Option>
                                        ))
                                    }
                                </Select>
                            </div>
                        )
                    }
                }
            ]
        }

        columns.push({
            key: 'Action',
            dataIndex: 'Action',
            title: 'Attribute Table',
            width: 120,
            render: (text, row) => {
                if(!ShapeFileHelper.isGeoJson(row?.fileName))
                    return '-'

                return (
                    <Button
                        size={'small'}
                        icon={<TableOutlined />}
                        onClick={() => {
                            openModal({
                                title: `${row?.fileName} Attributes`,
                                component: (
                                    <ShapeAttributeTable
                                        selectable={selectable}
                                        bounding={bounding}
                                        geoJson={row?.data}
                                        filters={row?.filters || {}}
                                        onChangeFilters={(prop, values) => {
                                            const shapeFiles = [...data.shapes]
                                            const index = shapeFiles.findIndex(x => x.id === row.id)
                                            if(index > -1) {
                                                if(!shapeFiles[index].filters)
                                                {
                                                    shapeFiles[index].filters = {}
                                                }

                                                shapeFiles[index].filters[prop] = values
                                                shapeFiles[index].data.id = v4()
                                            }
                                            setData({
                                                isChanged: v4(),
                                                shapes: shapeFiles
                                            })
                                        }}
                                        onChange={(geoJson) => {

                                        }}
                                    />
                                ),
                                closable: true,
                                style: {width: window.innerWidth*0.8}
                            })
                        }}
                    />
                )
            }
        })

        return columns
    }

    useEffect(() => {
        if(data.isChanged) {
            onChange && onChange([...data.shapes])
        }
    }, [data.isChanged]);

    return (
        <Table
            size={'small'}
            dataSource={data.shapes}
            pagination={false}
            scroll={{y: 500}}
            id={'fileName'}
            rowKey={'fileName'}
            rowClassName={'shape-item-row'}
            columns={getColumns()}
        />
    )
}